/**
 * Sonda UDP contra el TURN de la plataforma.
 *
 * Abre dos RTCPeerConnection en la misma pestaña, las dos forzadas a
 * `relay`, y las conecta entre si a traves del TURN. Los paquetes salen del
 * navegador, pasan por el mismo edge que despues transporta el RTP de las
 * llamadas y vuelven. De ahi salen perdida, jitter y latencia reales sobre
 * el path que importa.
 *
 * El DataChannel va sin orden y sin retransmisiones a proposito: con la
 * configuracion por defecto SCTP retransmite y tapa exactamente la perdida
 * que venimos a medir.
 *
 * Que el TURN no sea alcanzable tambien es un resultado, no un error: suele
 * ser un firewall corporativo bloqueando UDP, y esa es informacion util
 * para el que atiende el reclamo.
 */

import { averageOf, jitterOf } from './score';

export interface UdpProbeResult {
    ok: boolean;
    rttAvg: number | null;
    jitterAvg: number | null;
    lossPct: number | null;
    candidateType: string | null;
    sent: number;
    received: number;
    error: string | null;
}

const PACKETS_PER_SECOND = 20;
const DEFAULT_DURATION_MS = 8000;
const SETUP_TIMEOUT_MS = 8000;

const iceServers: RTCIceServer[] = [
    {
        urls: process.env.VUE_APP_TURN_URL || 'turn:stun1.myflexpbx.com:3478?transport=udp',
        username: process.env.VUE_APP_TURN_USERNAME || '',
        credential: process.env.VUE_APP_TURN_CREDENTIAL || '',
    },
];

export function failed(error: string): UdpProbeResult {
    return {
        ok: false,
        rttAvg: null,
        jitterAvg: null,
        lossPct: null,
        candidateType: null,
        sent: 0,
        received: 0,
        error,
    };
}

/**
 * Arma el resultado a partir de los RTT observados. Separado del armado de
 * la conexion para poder probarlo sin WebRTC.
 */
export function summarize(
    sent: number,
    rtts: number[],
    candidateType: string | null,
): UdpProbeResult {
    if (!sent) {
        return failed('sin paquetes enviados');
    }

    const received = rtts.length;

    return {
        ok: received > 0,
        rttAvg: averageOf(rtts),
        jitterAvg: jitterOf(rtts),
        lossPct: Math.round(((sent - received) / sent) * 1000) / 10,
        candidateType,
        sent,
        received,
        error: received > 0 ? null : 'ninguna respuesta del turn',
    };
}

function exchange(sender: RTCDataChannel, durationMs: number): Promise<number[]> {
    return new Promise((resolve) => {
        const startedAt = new Map<number, number>();
        const rtts: number[] = [];
        let seq = 0;

        // eslint-disable-next-line no-param-reassign
        sender.onmessage = (message) => {
            const echoed = Number(message.data);
            const sentAt = startedAt.get(echoed);

            if (sentAt !== undefined) {
                rtts.push(Math.round(performance.now() - sentAt));
                startedAt.delete(echoed);
            }
        };

        const timer = setInterval(() => {
            try {
                startedAt.set(seq, performance.now());
                sender.send(String(seq));
                seq += 1;
            } catch (err) {
                /* el canal murio a mitad de la sonda, se corta y se reporta lo medido */
            }
        }, 1000 / PACKETS_PER_SECOND);

        setTimeout(() => {
            clearInterval(timer);
            // se dan 500ms de gracia para los ecos que todavia estan en vuelo
            setTimeout(() => resolve(rtts), 500);
        }, durationMs);
    });
}

function supported(): boolean {
    return typeof window !== 'undefined' && typeof window.RTCPeerConnection === 'function';
}

/** Subconjunto de RTCStats que mira la sonda. */
interface StatsRow {
    type: string;
    id: string;
    state?: string;
    localCandidateId?: string;
    candidateType?: string;
}

async function candidateTypeOf(pc: RTCPeerConnection): Promise<string | null> {
    try {
        const stats = await pc.getStats();
        let pairId: string | null = null;
        const candidates = new Map<string, string>();

        stats.forEach((report: StatsRow) => {
            if (report.type === 'candidate-pair' && report.state === 'succeeded'
                && report.localCandidateId) {
                pairId = report.localCandidateId;
            }
            if (report.type === 'local-candidate' && report.candidateType) {
                candidates.set(report.id, report.candidateType);
            }
        });

        return pairId ? candidates.get(pairId) || null : null;
    } catch (err) {
        return null;
    }
}

function connect(pc1: RTCPeerConnection, pc2: RTCPeerConnection): void {
    /* eslint-disable no-param-reassign */
    pc1.onicecandidate = (event) => {
        if (event.candidate) {
            pc2.addIceCandidate(event.candidate).catch(() => undefined);
        }
    };
    pc2.onicecandidate = (event) => {
        if (event.candidate) {
            pc1.addIceCandidate(event.candidate).catch(() => undefined);
        }
    };
    /* eslint-enable no-param-reassign */
}

/**
 * Corre la sonda. Nunca lanza: devuelve un resultado con `ok: false` y el
 * motivo, porque un fallo de la sonda no puede tumbar el panel.
 */
export async function runUdpProbe(durationMs = DEFAULT_DURATION_MS): Promise<UdpProbeResult> {
    if (!supported()) {
        return failed('el navegador no soporta webrtc');
    }

    const config: RTCConfiguration = { iceServers, iceTransportPolicy: 'relay' };
    let pc1: RTCPeerConnection | null = null;
    let pc2: RTCPeerConnection | null = null;

    try {
        pc1 = new RTCPeerConnection(config);
        pc2 = new RTCPeerConnection(config);
        connect(pc1, pc2);

        const sender = pc1.createDataChannel('probe', { ordered: false, maxRetransmits: 0 });

        const echoReady = new Promise<void>((resolve) => {
            (pc2 as RTCPeerConnection).ondatachannel = (event) => {
                const receiver = event.channel;
                receiver.onmessage = (message) => {
                    try {
                        receiver.send(message.data);
                    } catch (err) {
                        /* el canal ya se cerro, no hay nada que hacer */
                    }
                };
                resolve();
            };
        });

        const offer = await pc1.createOffer();
        await pc1.setLocalDescription(offer);
        await pc2.setRemoteDescription(offer);
        const answer = await pc2.createAnswer();
        await pc2.setLocalDescription(answer);
        await pc1.setRemoteDescription(answer);

        const open = new Promise<void>((resolve, reject) => {
            sender.onopen = () => resolve();
            sender.onerror = () => reject(new Error('el datachannel no abrio'));
        });

        await Promise.race([
            Promise.all([open, echoReady]),
            new Promise((_resolve, reject) => {
                setTimeout(() => reject(new Error('el turn no respondio a tiempo')), SETUP_TIMEOUT_MS);
            }),
        ]);

        const rtts = await exchange(sender, durationMs);
        const candidateType = await candidateTypeOf(pc1);
        const expected = Math.floor((durationMs / 1000) * PACKETS_PER_SECOND);

        return summarize(expected, rtts, candidateType);
    } catch (err) {
        return failed((err as Error).message || 'la sonda udp fallo');
    } finally {
        if (pc1) {
            pc1.close();
        }
        if (pc2) {
            pc2.close();
        }
    }
}
